/**
 * Shared layout integration layer.
 *
 * Bridges the LayoutManager (from @frabr/fabric-editor) with Stimulus
 * controllers. Listens for layout panel events and delegates to the
 * LayoutManager.
 *
 * Usage:
 *   import { FabricLayout } from "../lib/fabric_layout";
 *
 *   const layout = new FabricLayout(editor, { onChanged: () => save() });
 *   layout.attach();
 *   // ... later
 *   layout.destroy();
 */
import type { FabricEditor } from "@frabr/fabric-editor";

// ── Types ───────────────────────────────────────────────────────────

interface FabricLayoutOpts {
  /** Called once after any layout mutation (drag-to-layout, panel edit, relayout). */
  onChanged?: () => void;
}

type LayoutMgr = FabricEditor["layout"];
type LayoutAction = (
  "setMode" | "setGap" | "setPadding" | "setOverflow" | "setAlignItems" | "setJustifyContent" | "setFlexDirection"
  | "makeContainer"
) & keyof LayoutMgr;

interface PanelEventConfig {
  action: LayoutAction;
  args: (detail: Record<string, unknown>) => unknown[] | null;
}

// Panel events → LayoutManager method + arg extraction from event detail
// Spacing lives on the container (gap + padding) — the flexbox model has
// no per-child margins. The size mode and the overflow also apply to texts.
const choice = (d: Record<string, unknown>) => (d.value ? [String(d.value)] : null);
const PANEL_EVENTS: Record<string, PanelEventConfig> = {
  "layout:set-mode":            { action: "setMode",           args: (d) => d.mode ? [d.mode] : null },
  "layout:set-gap":             { action: "setGap",            args: (d) => d.value != null ? [Number(d.value)] : null },
  "layout:set-padding":         { action: "setPadding",        args: (d) => d.side ? [d.side, Number(d.value)] : null },
  "layout:set-overflow":        { action: "setOverflow",       args: choice },
  "layout:set-align-items":     { action: "setAlignItems",     args: choice },
  "layout:set-justify-content": { action: "setJustifyContent", args: choice },
  "layout:set-direction":       { action: "setFlexDirection",  args: choice },
  "layout:make-container":      { action: "makeContainer",     args: () => [] },
};

// ── FabricLayout ────────────────────────────────────────────────────

export class FabricLayout {
  private editor: FabricEditor;
  private opts: FabricLayoutOpts;
  private boundHandler: (e: Event) => void;
  /** A panel edit is running: the LayoutManager's own callback would report it a second time. */
  private fromPanel = false;

  constructor(editor: FabricEditor, opts: FabricLayoutOpts = {}) {
    this.editor = editor;
    this.opts = opts;
    this.boundHandler = (e: Event) => this.handlePanelEvent(e as CustomEvent);
  }

  /** Wire up layout manager callbacks and panel event listeners. */
  attach(): void {
    this.editor.layout.setCallbacks({
      onLayoutCreated: () => this.opts.onChanged?.(),
      onLayoutChanged: () => {
        if (!this.fromPanel) this.opts.onChanged?.();
      },
    });

    for (const event of Object.keys(PANEL_EVENTS)) {
      window.addEventListener(event, this.boundHandler);
    }
  }

  /** Remove all event listeners. */
  destroy(): void {
    for (const event of Object.keys(PANEL_EVENTS)) {
      window.removeEventListener(event, this.boundHandler);
    }
    this.editor.layout.dispose();
  }

  // ── Panel event handler ───────────────────────────────────────────

  private handlePanelEvent(e: CustomEvent): void {
    const config = PANEL_EVENTS[e.type];
    if (!config) return;

    const args = config.args(e.detail || {});
    if (!args) return;

    const obj = this.editor.selection.current;
    if (!obj) return;

    this.fromPanel = true;
    try {
      (this.editor.layout[config.action] as Function).call(this.editor.layout, obj, ...args);
    } finally {
      this.fromPanel = false;
    }
    this.opts.onChanged?.();
  }
}
